import { AccountPositionRecord, AssetBalanceStatistics, FirmAssetBalanceStatistics } from '@tassat/fred-definitions';
import { AccountPositionRecordAssetExtended } from '@tassat/fred-extended-definitions';
import { createSelector } from 'reselect';

import { getAccountBalancesWithAssetId, getAccountPLedgeWithAssetId } from 'Lib/accountBalancesHelper';
import { AdminState } from 'Reducers/store/adminStore';
import { ClientState } from 'Reducers/store/clientStore';
import { ClientRoleEnum } from 'SharedComponents/users/ClientRoleEnum';

import { getAllAssetsByCurCodeAndPriceArray } from './assets';
import {
    getAccountPositions, getAssetBalanceStats, getFirmAssetEntitlements, getFirmAssetStats
} from './common';
import { getFirmAssetEntitlementsByFirmId } from './firmAssetEntitlements';
import { getMappedUserAccountsByUserIdClient } from './userAccounts';

const getAccountId = (_: AdminState | ClientState, accountId: string) => accountId;
const getFirmId = (_: AdminState | ClientState, firmId: string) => firmId;
const getRetrieveTransactionStats = (_: AdminState | ClientState, retrieveTransactionStats: boolean) => retrieveTransactionStats;
const getRetrieveCollateralStats =
    (_: AdminState | ClientState, _retrieveTransactionStats: boolean, retrieveCollateralStats: boolean) => retrieveCollateralStats;
const getSelectedAccount = (_: AdminState | ClientState, _firmId: string, selectedAccount: string | undefined) => selectedAccount;

const getAllAccountPositionArray = createSelector([getAccountPositions], (accountPositions) => {
    return Object.values(accountPositions)
        .reduce((acc, item): AccountPositionRecord[] => {
            const items = Object.values(item);

            return [ ...acc, ...items ];
        }, [] as AccountPositionRecord[]);
});

export const getAccountPositionsStats = createSelector(
    [
        getAllAssetsByCurCodeAndPriceArray,
        getAllAccountPositionArray,
        getRetrieveTransactionStats,
        getRetrieveCollateralStats,
        getFirmAssetEntitlements
    ],
    (assetsArray, accountPositionsArray, retrieveTransactionStats, retrieveCollateralStats, allEntitlements) => {
        const firmIds = new Set(accountPositionsArray.map(pos => pos.FirmId));
        const allowedTickers = new Set(
            Object.values(allEntitlements)
                .filter(entitlement => firmIds.has(entitlement.FirmId) && !entitlement.IsDeleted)
                .map(entitlement => entitlement.Ticker)
        );

        return assetsArray.map((item) => {
            const mappedAccountPositions = accountPositionsArray.reduce((acc, accountPos) => {
                if (Object.prototype.hasOwnProperty.call(item.securityAssets, accountPos.AssetId)) {
                    const asset = item.securityAssets[accountPos.AssetId];

                    if (!allowedTickers.has(asset.Ticker)) return acc;

                    acc.security.push({
                        ...accountPos,
                        Price: asset['Price'],
                        Ticker: asset['Ticker'],
                        Asset: asset
                    });
                } else {
                    const asset = item.ccyAssets[accountPos.AssetId];

                    if (!asset || !allowedTickers.has(asset.Ticker)) return acc;

                    acc.ccy.push({
                        ...accountPos,
                        Asset: asset
                    });
                }

                return acc;
            }, {
                ccy: [] as AccountPositionRecordAssetExtended[],
                security: [] as AccountPositionRecordAssetExtended[]
            });
            const security =
            getAccountBalancesWithAssetId(mappedAccountPositions.security, true, retrieveTransactionStats, retrieveCollateralStats);
            const ccy = getAccountBalancesWithAssetId(mappedAccountPositions.ccy, false, retrieveTransactionStats);

            return {
                curCode: item.curCode,
                symbol: item.symbol,
                ccy,
                security
            };
        });
    }
);

const getAllAssetBalanceStatsArray = createSelector([getAssetBalanceStats], (assetBalanceStats) => Object.values(assetBalanceStats));

const getFirmAssetStatsArray = createSelector([getFirmAssetStats], (firmAssetStats) => {
    return Object.values(firmAssetStats)
        .flatMap(firm => Object.values(firm));
});

export const getClientAccountPositionStats = createSelector(
    [ getMappedUserAccountsByUserIdClient, getAllAssetsByCurCodeAndPriceArray, getAllAccountPositionArray, getFirmAssetEntitlements ],
    (mappedUserAccounts, assetsArray, accountPositionsArray, allEntitlements) => {
        // Clients should only get balance records for accounts they are mapped to
        const usersMappedAccountPositions = accountPositionsArray.filter((accountPosition) => {
            const matchingAccountPosition = mappedUserAccounts
                .find((userAccount) => userAccount.userAccount.AccountId === accountPosition.AccountId &&
                    userAccount.userAccount.ClientRoleId !== ClientRoleEnum.NOT_ASSIGNED);

            return matchingAccountPosition;
        });

        const firmIds = new Set(usersMappedAccountPositions.map(pos => pos.FirmId));
        const allowedTickers = new Set(
            Object.values(allEntitlements)
                .filter(entitlement => firmIds.has(entitlement.FirmId) && !entitlement.IsDeleted)
                .map(entitlement => entitlement.Ticker)
        );

        return assetsArray.map((item) => {
            const mappedAccountPositions = usersMappedAccountPositions.reduce((acc, accountPos) => {
                if (Object.prototype.hasOwnProperty.call(item.securityAssets, accountPos.AssetId)) {
                    const asset = item.securityAssets[accountPos.AssetId];

                    if (!allowedTickers.has(asset.Ticker)) return acc;

                    acc.security.push({
                        ...accountPos,
                        Price: asset['Price'],
                        Ticker: asset['Ticker'],
                        Asset: asset
                    });
                } else {
                    const asset = item.ccyAssets[accountPos.AssetId];

                    if (!asset || !allowedTickers.has(asset.Ticker)) return acc;

                    acc.ccy.push({
                        ...accountPos,
                        Asset: asset
                    });
                }

                return acc;
            }, {
                ccy: [] as AccountPositionRecordAssetExtended[],
                security: [] as AccountPositionRecordAssetExtended[]
            });

            const security = getAccountBalancesWithAssetId(mappedAccountPositions.security, true, true);
            const ccy = getAccountBalancesWithAssetId(mappedAccountPositions.ccy, false, true);

            return {
                curCode: item.curCode,
                symbol: item.symbol,
                ccy,
                security
            };
        });
    }
);

export const getAccountPositionsStatsByAccountId = createSelector(
    [ getAllAssetsByCurCodeAndPriceArray, getAccountPositions, getAccountId, getFirmAssetEntitlements ],
    (assetsArray, accountPositions, accountId, allEntitlements) => {
        if (!accountPositions[accountId] || !Object.values(accountPositions).length) return;

        const accPositionsByAccountArray = Object.values(accountPositions[accountId]);
        const firmId = accPositionsByAccountArray[0]?.FirmId;

        const allowedTickers = new Set(
            Object.values(allEntitlements)
                .filter(entitlement => entitlement.FirmId === firmId && !entitlement.IsDeleted)
                .map(entitlement => entitlement.Ticker)
        );

        return assetsArray.map((item) => {
            const mappedAccountPositions = accPositionsByAccountArray.reduce((acc, accountPos) => {
                if (Object.prototype.hasOwnProperty.call(item.securityAssets, accountPos.AssetId)) {
                    const asset = item.securityAssets[accountPos.AssetId];

                    if (!allowedTickers.has(asset.Ticker)) return acc;

                    acc.security.push({
                        ...accountPos,
                        Price: asset['Price'],
                        Ticker: asset['Ticker'],
                        Asset: asset
                    });
                } else {
                    const asset = item.ccyAssets[accountPos.AssetId];

                    if (!asset || !allowedTickers.has(asset.Ticker)) return acc;

                    acc.ccy.push({
                        ...accountPos,
                        Asset: asset
                    });
                }

                return acc;
            }, {
                ccy: [] as AccountPositionRecordAssetExtended[],
                security: [] as AccountPositionRecordAssetExtended[]
            });

            const security = getAccountBalancesWithAssetId(mappedAccountPositions.security, true, false, true);
            const ccy = getAccountBalancesWithAssetId(mappedAccountPositions.ccy, false);

            return {
                curCode: item.curCode,
                symbol: item.symbol,
                ccy,
                security
            };
        });
    }
);

export const getAccountPositionsStatsByFirmId = createSelector(
    [ getAllAssetsByCurCodeAndPriceArray, getAllAccountPositionArray, getFirmId, getFirmAssetEntitlementsByFirmId ],
    (assetsArray, accountPositions, firmId, firmAssetEntitlements) => {
        const accPositionsByAccountArray = accountPositions.filter(position => position.FirmId === firmId);

        const allowedTickers = new Set(
            firmAssetEntitlements.map(entitlement => entitlement.Ticker)
        );

        return assetsArray.map((item) => {
            const mappedAccountPositions = accPositionsByAccountArray.reduce((acc, accountPos) => {
                if (Object.prototype.hasOwnProperty.call(item.securityAssets, accountPos.AssetId)) {
                    const asset = item.securityAssets[accountPos.AssetId];

                    if (!allowedTickers.has(asset.Ticker)) return acc;

                    acc.security.push({
                        ...accountPos,
                        Price: asset['Price'],
                        Ticker: asset['Ticker'],
                        Asset: asset
                    });
                } else {
                    const asset = item.ccyAssets[accountPos.AssetId];

                    if (!asset || !allowedTickers.has(asset.Ticker)) return acc;

                    acc.ccy.push({
                        ...accountPos,
                        Asset: asset
                    });
                }

                return acc;
            }, {
                ccy: [] as AccountPositionRecordAssetExtended[],
                security: [] as AccountPositionRecordAssetExtended[]
            });

            const security = getAccountBalancesWithAssetId(mappedAccountPositions.security, true);
            const ccy = getAccountBalancesWithAssetId(mappedAccountPositions.ccy, false);

            return {
                curCode: item.curCode,
                symbol: item.symbol,
                ccy,
                security
            };
        });
    }
);

export const getAccountPositionPledgeAmountByFirmId = createSelector([
    getAllAssetsByCurCodeAndPriceArray,
    getAllAccountPositionArray,
    getAllAssetBalanceStatsArray,
    getFirmAssetStatsArray,
    getFirmId,
    getSelectedAccount
],
(assetsArray, accountPositions, assetBalanceStats, firmAssetStats, firmId, selectedAccount) => {
    const positionsArray: (AccountPositionRecord | FirmAssetBalanceStatistics | AssetBalanceStatistics)[] =
        selectedAccount !== 'none' ? accountPositions : firmId !== 'none' ? firmAssetStats : assetBalanceStats;

    return assetsArray.map((item) => {
        const mappedAccountPositions = positionsArray.reduce((acc, accountPos) => {
            if (!item.securityAssets[accountPos.AssetId]) return acc;

            if (firmId !== 'none' && 'FirmId' in accountPos && accountPos.FirmId !== firmId) {
                return acc;
            }

            if (selectedAccount !== 'none' && 'AccountId' in accountPos && accountPos.AccountId !== selectedAccount) {
                return acc;
            }

            acc.security.push({
                ...(accountPos as AccountPositionRecord),
                Price: item.securityAssets[accountPos.AssetId]['Price'],
                Ticker: item.securityAssets[accountPos.AssetId]['Ticker'],
                Asset: item.securityAssets[accountPos.AssetId]
            });

            return acc;
        }, { security: [] as AccountPositionRecordAssetExtended[] });

        const security = getAccountPLedgeWithAssetId(mappedAccountPositions.security);

        return {
            curCode: item.curCode,
            symbol: item.symbol,
            security
        };
    });
});